import Cookies from 'universal-cookie'
import allowlist from './allowlist.json'

/**
 * STY-725 / P-STY-12 phase 1: first-party funnel tracker.
 * Plan: smp_blog docs/plans/STY-719.md. Endpoint: POST /api/v2/funnel/events.
 *
 * - Vendor actions only. App.js turns the tracker off for admins and editors,
 *   and the server skips staff anyway. Before configureTracker() is called,
 *   events wait in memory; disabled, they are dropped.
 * - Events queue in memory and flush every 5 seconds, at most 50 per request.
 * - On pagehide the queue is stashed in localStorage BEFORE it's sent
 *   (keepalive fetch, falling back to sendBeacon). A true return from either
 *   only means "queued", so the stash is resent on the next load and cleared
 *   once the server acknowledges it. event_id makes retries idempotent.
 * - It never throws, and nothing awaits it on a UI path.
 */

const STASH_KEY = 'smp_funnel_pending'
const FLUSH_MS = 5000
const MAX_BATCH = 50
const MAX_BATCH_BYTES = 30000 // under the server's 32KB
const MAX_QUEUE = 500
// As large as the queue, so nothing queued is ever dropped from the stash (Codex on #113).
const MAX_STASH = MAX_QUEUE
const MAX_ATTEMPTS = 3
// A fetch can stay pending for minutes or forever. Past this, the request counts
// as failed: it retries within the attempt budget and frees the flush lock.
const SEND_TIMEOUT_MS = 20000

// Sent first when the page is going away and bandwidth is limited.
const PRIORITY = {
  builder_session_ended: 0,
  submit_failed: 1,
  upload_failed: 1,
  save_failed: 1,
  credit_rejected: 1,
  images_rejected: 1,
  submission_create_failed: 1,
  session_expired: 1,
  section_viewed: 3
}

const cookies = new Cookies()

function browserEnv () {
  const w = typeof window !== 'undefined' ? window : {}
  return {
    fetch: w.fetch ? w.fetch.bind(w) : null,
    sendBeacon: w.navigator && w.navigator.sendBeacon ? w.navigator.sendBeacon.bind(w.navigator) : null,
    storage: guardedStorage(w),
    now: () => Date.now(),
    uuid: randomUuid,
    location: w.location || { pathname: '' },
    viewportWidth: () => w.innerWidth || 0,
    isOnline: () => !(w.navigator && w.navigator.onLine === false),
    getToken: () => cookies.get('smp_access_token') || (w.sessionStorage && w.sessionStorage.getItem('token')) || '',
    setInterval: (fn, ms) => w.setInterval(fn, ms),
    clearInterval: (id) => w.clearInterval(id),
    setTimeout: (fn, ms) => w.setTimeout(fn, ms),
    clearTimeout: (id) => w.clearTimeout(id)
  }
}

// Reading window.localStorage itself throws (SecurityError) when site storage is
// blocked: sandboxed frames, some privacy settings. This runs at import time,
// so an unguarded read would stop the whole app loading (Codex P1, #113).
// Every access goes through try/catch; with no storage, stashing is skipped.
function guardedStorage (w) {
  const store = () => { try { return w.localStorage || null } catch (e) { return null } }
  return {
    getItem: (k) => { const s = store(); return s ? s.getItem(k) : null },
    // Throws without storage, so a failed stash write is visible to the caller.
    setItem: (k, v) => { const s = store(); if (!s) throw new Error('no storage'); s.setItem(k, v) },
    removeItem: (k) => { const s = store(); if (s) s.removeItem(k) }
  }
}

function randomUuid () {
  const c = typeof window !== 'undefined' && (window.crypto || window.msCrypto)
  const bytes = new Uint8Array(16)
  if (c && c.getRandomValues) c.getRandomValues(bytes)
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.prototype.map.call(bytes, (b) => (b + 0x100).toString(16).slice(1)).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

let env = browserEnv()
let state = freshState()
// Outside state, so turning the tracker off and on again keeps a mounted
// builder's session end on pagehide (review on #113).
let hooks = []

// ctx is the authorization context: { endpoint, stashKey, token, accountId }.
// It is never mutated. A context switch installs a NEW object, so "is this
// request's context still current?" is just state.ctx === ctx, and every send
// carries its own endpoint, stash key and token instead of reading state after
// an await (review on #113).
//
// parked: events that belong in the stash but couldn't be written (storage
// full or blocked). Kept in memory for this page's unload send, never resent
// by the timer.
function freshState () {
  return { mode: 'pending', ctx: null, queue: [], parked: [], sessionId: null, appVersion: null, siteUrl: null, timer: null, inFlight: false, inFlightBatch: [] }
}

function safe (fn, fallback) {
  try { return fn() } catch (e) { return fallback }
}

function deviceClass (width) {
  if (!width) return undefined
  if (width < 768) return 'mobile'
  if (width < 1024) return 'tablet'
  return 'desktop'
}

// A page type, never a key: no vendor email (dashboard), no share-link hash,
// no ids (STY-725, Codex on #113). The server normalizes again.
export function normalizeRoute (pathname) {
  if (typeof pathname !== 'string' || !pathname) return undefined
  return pathname
    .replace(/^\/submissions\/dashboard\/[^/]+/, '/submissions/dashboard/:email')
    .replace(/^(\/submissions\/submission\/\d+)\/[^/]+/, '$1/:hash')
    .replace(/\/\d+(?=\/|$)/g, '/:id')
}

function cleanProps (props) {
  const out = {}
  Object.keys(props || {}).forEach((key) => {
    const value = props[key]
    if (value !== null && value !== undefined) out[key] = value
  })
  return out
}

// ---- The stash: one per context, in localStorage. Every helper takes the key.

function readStash (key) {
  return safe(() => {
    const raw = env.storage.getItem(key)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  }, [])
}

// The one place the stash cap applies. Over the cap, the lowest-priority
// events go first (section_viewed before a failure or a session end), keeping
// order otherwise. Returns false if anything was cut or the write failed
// (QuotaExceeded, private mode, blocked storage), so callers keep their events
// in memory instead (review on #113).
function writeStash (events, key) {
  let kept = events
  if (events.length > MAX_STASH) {
    // Decorated sort: Array#sort isn't stable on Node 10.
    kept = events.map((e, i) => ({ e, i }))
      .sort((a, b) => (priorityOf(a.e.event_name) - priorityOf(b.e.event_name)) || (a.i - b.i))
      .slice(0, MAX_STASH)
      .sort((a, b) => a.i - b.i)
      .map((x) => x.e)
  }
  const written = safe(() => {
    if (kept.length) env.storage.setItem(key, JSON.stringify(kept))
    else env.storage.removeItem(key)
    return true
  }, false)
  return written && kept.length === events.length
}

function unstash (ids, key) {
  if (!ids.length) return
  const done = new Set(ids)
  const stash = readStash(key)
  const left = stash.filter((e) => !done.has(e.event_id))
  if (left.length !== stash.length) writeStash(left, key)
}

// Add events without duplicating any already there. front: put them first
// (the unload path writes the newest, highest-priority events first).
function mergeIntoStash (events, key, front) {
  if (!events.length) return true
  const stash = readStash(key)
  const known = new Set(stash.map((e) => e.event_id))
  const fresh = events.filter((e) => !known.has(e.event_id))
  if (!fresh.length) return true
  return writeStash(front ? fresh.concat(stash) : stash.concat(fresh), key)
}

function dedupe (events) {
  const seen = new Set()
  return events.filter((e) => (seen.has(e.event_id) ? false : seen.add(e.event_id)))
}

// Events that should wait for the next load. If storage won't take them and
// their context is still current, they stay in memory for the unload send.
function park (ctx, events) {
  if (!ctx || !events.length) return
  if (mergeIntoStash(events, ctx.stashKey)) return
  if (state.ctx === ctx) state.parked = dedupe(state.parked.concat(events))
}

// Nothing is ever cut off the end of the queue: overflow goes to the stash
// (review on #113).
function capQueue () {
  if (state.queue.length <= MAX_QUEUE) return
  const overflow = state.queue.slice(MAX_QUEUE)
  state.queue = state.queue.slice(0, MAX_QUEUE)
  park(state.ctx, overflow.map((w) => w.event))
}

function takeBatch (events) {
  const batch = []
  let bytes = 20
  while (events.length && batch.length < MAX_BATCH) {
    const size = JSON.stringify(events[0]).length + 1
    if (batch.length && bytes + size > MAX_BATCH_BYTES) break
    bytes += size
    batch.push(events.shift())
  }
  return batch
}

// The page's route as events record it. Callers pass it explicitly when an
// event belongs to a page the vendor may already have left.
export function currentRoute () {
  return safe(() => normalizeRoute(env.location.pathname), undefined)
}

// ---- Sending. Every path builds its request here and reads the outcome with
// classify(), so ordinary, context-drain and unload sends agree on what a
// response means (review on #113).

// The Bearer token (captured for this context) or the share link's hash is the
// identity. credentials: 'omit', so a cookie never stands in for it: with
// cookies, a share-link session or a stale token would go out under whoever is
// signed in on this browser now (review on #113).
function requestInit (ctx, batch, keepalive) {
  const headers = { 'Content-Type': 'application/json' }
  if (ctx.token) headers.Authorization = `Bearer ${ctx.token}`
  const init = { method: 'POST', headers, body: JSON.stringify({ events: batch }), credentials: 'omit' }
  if (keepalive) init.keepalive = true
  return init
}

// One ordinary POST, the whole exchange (headers AND body) within
// SEND_TIMEOUT_MS: a body that stalls after the headers must not hold the flush
// lock forever (review on #113). Resolves { status, json }; rejects on network
// error, timeout, or a 202 whose body can't be read: an acknowledgement nobody
// could read isn't one, so the batch retries (local Codex on #113).
function postEvents (ctx, batch) {
  let timer = null
  const timeout = new Promise((resolve, reject) => {
    timer = env.setTimeout(() => reject(new Error('funnel send timed out')), SEND_TIMEOUT_MS)
  })
  const exchange = Promise.resolve()
    .then(() => env.fetch(ctx.endpoint, requestInit(ctx, batch)))
    .then((res) => (res.status === 202
      ? Promise.resolve(res.json()).then((json) => ({ status: 202, json }))
      : { status: res.status, json: null }))
  const done = () => safe(() => env.clearTimeout(timer))
  return Promise.race([exchange, timeout]).then((r) => { done(); return r }, (e) => { done(); throw e })
}

/**
 * What a response means for each event in the batch. outcome is null for a
 * network error or timeout.
 * - done: acknowledged (accepted, duplicate, rejected, skipped) or permanently
 *   refused (4xx such as 400, or 404 before the endpoint is deployed). Leaves
 *   the stash.
 * - retry: transient, so tried again this page within MAX_ATTEMPTS, then kept
 *   for the next load. Network errors, timeouts, 429, 5xx, and per-event
 *   "error": the server answers 202 even when its database is down, so a
 *   per-event error is an outage too (review on #113). Retries can't go on
 *   forever: the server refuses events over 30 days old, which acknowledges
 *   them, and the stash holds MAX_STASH at most.
 * - park: 401, usually a login about to be refreshed. Kept for the next load,
 *   not retried into a wall.
 */
function classify (batch, outcome) {
  const ids = batch.map((e) => e.event_id)
  if (!outcome) return { done: [], retry: ids, park: [] }
  const { status, json } = outcome
  if (status === 202) {
    // Only what the server confirmed is done. An event it errored on, or left
    // out of the results, is retried (local Codex on #113).
    const results = (json && json.data && Array.isArray(json.data.results)) ? json.data.results : []
    const confirmed = new Set(results.filter((r) => r && r.status !== 'error').map((r) => r.event_id))
    return { done: ids.filter((id) => confirmed.has(id)), retry: ids.filter((id) => !confirmed.has(id)), park: [] }
  }
  if (status === 401) return { done: [], retry: [], park: ids }
  if (status === 429 || status >= 500) return { done: [], retry: ids, park: [] }
  return { done: ids, retry: [], park: [] }
}

// Apply a classified outcome for a flush. Retries go back to the front of the
// queue if this request's context is still current; otherwise, and once out of
// attempts, they wait in the context's own stash.
function settle (ctx, wrapped, result) {
  const byId = {}
  wrapped.forEach((w) => { byId[w.event.event_id] = w })
  unstash(result.done, ctx.stashKey)
  const retry = []
  const later = result.park.map((id) => byId[id].event)
  result.retry.forEach((id) => {
    const w = byId[id]
    if (++w.attempts < MAX_ATTEMPTS && state.ctx === ctx) retry.push(w)
    else later.push(w.event)
  })
  park(ctx, later)
  if (!retry.length) return
  const ids = new Set(retry.map((w) => w.event.event_id))
  state.queue = retry.concat(state.queue.filter((w) => !ids.has(w.event.event_id)))
  capQueue()
}

/**
 * Leave the current context (a route switch, another account, or turning off).
 * Its queue goes under the OLD context: sent now by ordinary fetch when the
 * login is the same (not keepalive, whose 64KB budget is for unload), or only
 * stashed when it isn't. The batch in flight is stashed there too and gives up
 * the flush lock, so the next context neither sends it under the wrong
 * identity nor waits on it (Codex on #113).
 */
function leaveContext ({ sendNow }) {
  const old = state.ctx
  const leaving = dedupe(state.queue.map((w) => w.event).concat(state.parked))
  state.queue = []
  state.parked = []
  if (sendNow) sendToContext(leaving, old)
  else mergeIntoStash(leaving, old.stashKey)
  mergeIntoStash(state.inFlightBatch, old.stashKey)
  state.inFlightBatch = []
  state.inFlight = false
}

// Send events under a context the tracker has left: stashed there first, then
// sent batch after batch, each cleared from the stash as it's acknowledged.
// Anything not acknowledged stays stashed for that context's next visit.
function sendToContext (events, ctx) {
  if (!events.length || !ctx) return Promise.resolve()
  mergeIntoStash(events, ctx.stashKey)
  if (!env.fetch) return Promise.resolve()
  const remaining = events.slice()
  const next = () => {
    if (!remaining.length) return
    const batch = takeBatch(remaining)
    return postEvents(ctx, batch).then((outcome) => {
      unstash(classify(batch, outcome).done, ctx.stashKey)
      if (outcome.status === 202) return next()
    })
  }
  return next().catch(() => {})
}

// One stash and endpoint per authorization context: per signed-in account, so
// events left by one vendor are never sent under another's login on a shared
// browser; and per share link, since a link authorizes only its own submission
// (local Codex review).
function makeContext (siteUrl, publicLink, accountId, token) {
  let endpoint = `${siteUrl}/api/v2/funnel/events`
  let stashKey = accountId ? `${STASH_KEY}:user:${accountId}` : STASH_KEY
  if (publicLink && publicLink.submissionId && publicLink.hash) {
    endpoint += `?submission_id=${encodeURIComponent(publicLink.submissionId)}&public=${encodeURIComponent(publicLink.hash)}`
    stashKey += `:public:${publicLink.submissionId}`
  }
  return { endpoint, stashKey, token: token || '', accountId }
}

function sameTarget (a, b) {
  return Boolean(a && b && a.endpoint === b.endpoint && a.stashKey === b.stashKey)
}

// Events a previous page left in this context's stash go to the front.
function restoreStash () {
  const known = new Set(state.queue.map((w) => w.event.event_id).concat(state.inFlightBatch.map((e) => e.event_id)))
  const stashed = readStash(state.ctx.stashKey).filter((e) => !known.has(e.event_id)).map((event) => ({ event, attempts: 0 }))
  state.queue = stashed.concat(state.queue)
  capQueue()
}

/**
 * Call once the user is known. `enabled: false` (admins and editors) drops
 * everything. `publicLink` is set on the collaborator share link. `token` is
 * the one App used to identify the user; it falls back to the live cookie.
 */
export function configureTracker ({ enabled, siteUrl, appVersion, publicLink, accountId, token }) {
  safe(() => {
    if (state.timer) env.clearInterval(state.timer)
    state.timer = null
    if (!enabled) {
      // Was on for a vendor (an admin then signed in, in the same tab): their
      // queue waits in their stash. ctx becomes null, so a request still in
      // flight settles into its own stash, never into what's configured next.
      if (state.mode === 'on' && state.ctx) leaveContext({ sendNow: false })
      state = { ...freshState(), mode: 'off', sessionId: state.sessionId }
      return
    }

    // The token App identified the user with, not one read later: if the cookie
    // changed during that request, this account's events must not go out under
    // the new login (review on #113).
    const captured = token !== undefined ? token : safe(() => env.getToken(), '')
    let ctx = makeContext(siteUrl, publicLink, accountId, captured)
    if (state.mode === 'on' && state.ctx) {
      if (sameTarget(ctx, state.ctx) && ctx.token === state.ctx.token) ctx = state.ctx
      // Same stash and endpoint with a refreshed token: adopt it. A request in
      // flight with the old token settles into the same stash.
      else if (!sameTarget(ctx, state.ctx)) leaveContext({ sendNow: state.ctx.accountId === accountId })
    }

    const wasOn = state.mode === 'on'
    state.mode = 'on'
    state.siteUrl = siteUrl
    state.appVersion = appVersion || null
    state.sessionId = state.sessionId || env.uuid()
    if (!wasOn) {
      // Events tracked before configuration get the version and session now.
      state.queue.forEach((w) => {
        w.event.app_version = state.appVersion
        w.event.session_id = w.event.session_id || state.sessionId
      })
    }
    if (state.ctx !== ctx) {
      state.ctx = ctx
      restoreStash()
    }
    state.timer = env.setInterval(() => { flush() }, FLUSH_MS)
  })
}

/**
 * The route changed inside the app, for example from a share link to the
 * dashboard. If that changes the context, everything queued is sent (and
 * stashed) under the OLD context first, then the new context's stash is
 * restored (Codex on #113).
 */
export function retargetTracker ({ publicLink }) {
  safe(() => {
    if (state.mode !== 'on' || !state.ctx) return
    const next = makeContext(state.siteUrl, publicLink, state.ctx.accountId, state.ctx.token)
    if (sameTarget(next, state.ctx)) return
    leaveContext({ sendNow: true })
    state.ctx = next
    restoreStash()
  })
}

export function getSessionId () {
  return state.sessionId
}

export function rotateSession () {
  state.sessionId = env.uuid()
  return state.sessionId
}

/**
 * Run fn on pagehide BEFORE the queue is stashed and sent, such as ending the
 * builder session, so its event leads the first unload request instead of
 * following in a later one (Codex on #113). Returns an unsubscribe function.
 */
export function onBeforePagehide (fn) {
  hooks.push(fn)
  return () => { hooks = hooks.filter((h) => h !== fn) }
}

function priorityOf (name) {
  return PRIORITY[name] !== undefined ? PRIORITY[name] : 2
}

export function track (name, props, submissionId, options) {
  safe(() => {
    if (state.mode === 'off') return
    if (!allowlist.events[name] || allowlist.events[name].server_only) return

    if (!state.sessionId) state.sessionId = env.uuid()
    const width = safe(() => env.viewportWidth(), 0)
    const event = {
      event_id: env.uuid(),
      event_name: name,
      occurred_at: new Date(env.now()).toISOString(),
      submission_id: submissionId ? parseInt(submissionId, 10) : undefined,
      session_id: (options && options.sessionId) || state.sessionId,
      app: 'react-submissions',
      app_version: state.appVersion,
      allowlist_version: allowlist.version,
      route: options && options.route !== undefined ? options.route : currentRoute(),
      device_class: deviceClass(width),
      viewport_w: width || undefined,
      props: cleanProps(props)
    }
    Object.keys(event).forEach((k) => { if (event[k] === undefined || event[k] === null) delete event[k] })

    // The queue counts the batch in flight, so a failed send can always requeue
    // it (local Codex review). Full: the session end evicts the newest
    // lowest-priority event (Codex on #113); anything else waits in the stash.
    // Both go to the stash, not nowhere.
    if (state.queue.length + state.inFlightBatch.length >= MAX_QUEUE) {
      const victim = priorityOf(name) === 0 ? evictionCandidate() : -1
      if (victim === -1) { park(state.ctx, [event]); return }
      park(state.ctx, [state.queue.splice(victim, 1)[0].event])
    }
    state.queue.push({ event, attempts: 0 })
  })
}

// The newest event of the lowest priority, never another session end.
function evictionCandidate () {
  let victim = -1
  state.queue.forEach((w, i) => {
    if (victim === -1 || priorityOf(w.event.event_name) >= priorityOf(state.queue[victim].event.event_name)) victim = i
  })
  return victim !== -1 && priorityOf(state.queue[victim].event.event_name) !== 0 ? victim : -1
}

export function flush () {
  return Promise.resolve().then(() => {
    if (state.mode !== 'on' || !state.ctx || state.inFlight || !state.queue.length || !env.fetch) return
    // Offline: wait. Sending now would only spend the retry budget (review on #113).
    if (env.isOnline && !safe(() => env.isOnline(), true)) return

    const ctx = state.ctx
    const wrapped = takeBatch(state.queue)
    const batch = wrapped.map((w) => w.event)
    // The lock is this request's own token. A pagehide or context switch that
    // takes over the batch releases it, so a request that never settles can't
    // block every later flush (local Codex review). inFlightBatch is kept until
    // the response, so a pagehide mid-request can still stash and resend it
    // (Codex P1, #113).
    const lock = {}
    state.inFlight = lock
    state.inFlightBatch = batch
    const release = () => {
      if (state.inFlight !== lock) return
      state.inFlight = false
      state.inFlightBatch = []
    }

    return postEvents(ctx, batch)
      .then((outcome) => settle(ctx, wrapped, classify(batch, outcome)), () => settle(ctx, wrapped, classify(batch, null)))
      .then(release, release)
  }).catch(() => {})
}

/**
 * The page may be going away (pagehide, or hidden on mobile). Stash everything
 * first, then send by keepalive fetch, or by sendBeacon. Safe to call
 * repeatedly: each call sends only what has been tracked since the last one.
 *
 * One batch per call by default: the hidden flush and the pagehide flush are
 * each 30KB or less, so together they fit the browser's 64KB unload budget.
 * The highest-priority events (the session end first) go in that batch.
 * Whatever isn't sent stays queued in memory and in the stash (Codex on #113).
 * Before configure there's no context to stash under, so the queue just stays
 * in memory (review on #113).
 */
export function flushOnPagehide (maxBatches = 1) {
  safe(() => {
    if (state.mode !== 'on' || !state.ctx) return
    const ctx = state.ctx

    const pending = dedupe(state.inFlightBatch.concat(state.queue.map((w) => w.event), state.parked))
    // This flush takes over the batch in flight, and with it the lock. If the
    // original request settles too, event_id makes the second copy harmless.
    if (state.inFlightBatch.length) state.inFlight = false
    state.inFlightBatch = []
    if (!pending.length) return
    pending.sort((a, b) => priorityOf(a.event_name) - priorityOf(b.event_name))

    const stored = mergeIntoStash(pending, ctx.stashKey, true)
    const remaining = pending.slice()
    let batches = 0
    while (remaining.length && batches++ < maxBatches) sendWhileLeaving(ctx, takeBatch(remaining))

    // Stashed: memory keeps only what wasn't sent. Not stashed (storage full or
    // blocked): memory is the only copy, so the sent batch stays too, and parked
    // events stay parked.
    const parkedIds = new Set(state.parked.map((e) => e.event_id))
    const keep = (stored ? remaining : pending).filter((e) => !parkedIds.has(e.event_id))
    if (stored) state.parked = []
    state.queue = keep.map((event) => ({ event, attempts: 0 }))
    capQueue()
  })
}

// keepalive fetch carries the Bearer header; sendBeacon is the fallback. If the
// page is still alive to read a 202 (a hidden tab, not a closed one), the batch
// leaves the stash, as on every other send path (audit on #113).
function sendWhileLeaving (ctx, batch) {
  // A beacon authenticates by the live cookie, so it's sent only while that
  // cookie is still this context's token (review on #113).
  const beacon = () => {
    if (env.sendBeacon && safe(() => (env.getToken() || '') === ctx.token, false)) {
      safe(() => env.sendBeacon(ctx.endpoint, new Blob([JSON.stringify({ events: batch })], { type: 'text/plain;charset=UTF-8' })))
    }
  }
  let started = false
  if (env.fetch) {
    started = safe(() => {
      // An async rejection (budget exceeded, network) also falls back to the
      // beacon (local Codex review). The stash covers whatever neither delivers.
      Promise.resolve(env.fetch(ctx.endpoint, requestInit(ctx, batch, true)))
        .then((res) => {
          if (!res || res.status !== 202) return
          return Promise.resolve(res.json()).then((json) => {
            const done = classify(batch, { status: 202, json }).done
            unstash(done, ctx.stashKey)
            // Without storage the batch also stayed in memory (queued or
            // parked); it's delivered now, so it leaves both (local Codex on #113).
            const ids = new Set(done)
            if (state.ctx === ctx) {
              state.queue = state.queue.filter((w) => !ids.has(w.event.event_id))
              state.parked = state.parked.filter((e) => !ids.has(e.event_id))
            }
          }, () => {})
        }, beacon)
        .catch(() => {})
      return true
    }, false)
  }
  if (!started) beacon()
}

// Wire the browser lifecycle once. visibilitychange to hidden is the last event
// that reliably fires on mobile; pagehide covers desktop navigation.
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('pagehide', () => {
    hooks.slice().forEach((hook) => safe(() => hook()))
    flushOnPagehide()
  })
  // Hidden is not necessarily leaving, and a pagehide may follow with
  // builder_session_ended. So send at most one batch (30KB or less) here,
  // leaving the browser's 64KB unload budget room for that event. Anything over
  // is already stashed and goes on the next load (Codex on #113).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnPagehide()
  })
}

export function _resetForTests (testEnv) {
  env = { ...browserEnv(), ...testEnv }
  state = freshState()
  hooks = []
}
